import React, { Fragment, useState, useEffect } from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  inputBaseClasses,
} from '@mui/material';
import Button from 'components/Button/Button';
import { QuoteLineItem } from 'features/salesforce/salesforceTypes';

interface ActiveProductFormProps {
  previewedProduct: QuoteLineItem;
  quoteName: string;
  isSaveLoading: boolean;
  onChangeSalesPrice: (newSales: string) => void;
  onChangeQuantity: (newQuantity: string) => void;
  onChangeDiscount: (newDiscount: string) => void;
  onChangeDescription: (newDescription: string) => void;
  onClickCancel: () => void;
  onSave: (replaceProductName: boolean) => void;
}

const commonTextFieldStyles = {
  [`& .${inputBaseClasses.root}`]: {
    borderRadius: '8px',
    height: '48px',
  },
};

export const ActiveProductForm = ({
  previewedProduct,
  quoteName,
  isSaveLoading,
  onChangeDescription,
  onChangeDiscount,
  onChangeQuantity,
  onChangeSalesPrice,
  onClickCancel,
  onSave,
}: ActiveProductFormProps) => {
  const { Description, Name, ListPrice, UnitPrice, TotalPrice, Quantity, Discount, Subtotal, lineItemNumber } =
    previewedProduct;

  const [changeProductName, setChangeProductName] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  useEffect(() => {
    const isEmptyField = UnitPrice === 0;
    const isNegativeNumber = Quantity < 0 || Discount < 0 || UnitPrice < 0;
    setIsSaveDisabled(isEmptyField || isNegativeNumber || isSaveLoading);
  }, [UnitPrice, Quantity, Discount, isSaveLoading]);

  return (
    <Fragment>
      <Typography fontSize={'16px'} fontWeight={500}>
        Pricing Table Row
      </Typography>
      <Typography fontSize={'14px'} fontWeight={500} marginTop={'32px'}>
        Required Information
      </Typography>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'24px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Line Item Number
        </Typography>
        <TextField
          disabled
          value={lineItemNumber}
          fullWidth
          sx={{
            ...commonTextFieldStyles,
          }}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Product
        </Typography>
        <TextField
          disabled
          value={Name}
          fullWidth
          sx={{
            ...commonTextFieldStyles,
          }}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Quote Name
        </Typography>
        <TextField
          disabled
          value={quoteName}
          fullWidth
          sx={{
            ...commonTextFieldStyles,
          }}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          List Price
        </Typography>
        <TextField
          disabled
          value={ListPrice}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          sx={{
            ...commonTextFieldStyles,
          }}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Sales Price
        </Typography>
        <TextField
          value={UnitPrice}
          fullWidth
          type='number'
          InputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          sx={{
            ...commonTextFieldStyles,
          }}
          onChange={e => onChangeSalesPrice(e.target.value)}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Quantity
        </Typography>
        <TextField
          value={Quantity}
          fullWidth
          type='number'
          sx={{
            ...commonTextFieldStyles,
          }}
          onChange={e => onChangeQuantity(e.target.value)}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Subtotal
        </Typography>
        <TextField
          disabled
          value={Subtotal}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          sx={{
            ...commonTextFieldStyles,
          }}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Discount (percentage)
        </Typography>
        <TextField
          value={Discount || 0}
          fullWidth
          type='number'
          InputProps={{
            startAdornment: <InputAdornment position='start'>%</InputAdornment>,
          }}
          sx={{
            ...commonTextFieldStyles,
          }}
          onChange={e => onChangeDiscount(e.target.value)}
        />
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'16px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Total Price
        </Typography>
        <TextField
          disabled
          value={TotalPrice}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          sx={{
            ...commonTextFieldStyles,
          }}
        />
      </Box>
      <Divider sx={{ color: '#C9CACB', width: '100%', marginTop: '32px' }} />
      <Typography fontSize={'14px'} fontWeight={500} marginTop={'24px'}>
        Optional Information
      </Typography>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        gap={'16px'}
        marginTop={'24px'}
        width={'100%'}
      >
        <Typography fontSize={'14px'} fontWeight={400}>
          Line Item Description
        </Typography>
        <TextField
          value={Description || ''}
          fullWidth
          sx={{
            ...commonTextFieldStyles,
          }}
          onChange={e => onChangeDescription(e.target.value)}
        />
        <FormControlLabel
          className='activeProductFormLabel'
          control={
            <Switch
              checked={changeProductName}
              color='secondary'
              onChange={() => {
                setChangeProductName(prevState => !prevState);
              }}
            />
          }
          label='Replace Product Name'
        />
      </Box>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        marginTop={'24px'}
        marginBottom={'48px'}
        width={'100%'}
      >
        <Button disabled={isSaveLoading} variant='secondary' onClick={onClickCancel}>
          Cancel
        </Button>
        <Button disabled={isSaveDisabled} onClick={() => onSave(changeProductName)}>
          Save
        </Button>
      </Box>
    </Fragment>
  );
};
